import { Button } from '../../../components/Button';
import { Icon } from '../../../components/icons/Icon';
import { useTranslation } from '../../../providers/UIProvider';
import type { BannerIssue } from './types';

export interface IssueRowProps {
  issue: BannerIssue;
  onScrollTo?: (target: string) => void;
}

/** A single flat row inside a severity section. */
export function IssueRow({ issue, onScrollTo }: IssueRowProps) {
  const t = useTranslation('components.validationBanner');
  const fieldLabel = issue.fieldLabel ?? issue.path ?? '';

  return (
    <div className='flex items-start gap-sm py-2xs group/row'>
      <div className='text-xs leading-snug'>
        <p>
          {fieldLabel && (
            <>
              <span className='font-semibold text-foreground-base'>{fieldLabel}</span>
              <span className='text-foreground-muted mx-xs'>—</span>
            </>
          )}
          <span className='text-foreground-muted'>{issue.message}</span>
          {issue.comparison && (
            <span className='inline-flex items-center gap-xs ml-sm flex-wrap'>
              <span className='inline-flex items-center gap-xs font-mono bg-black/8 rounded px-xs py-px'>
                <span className='text-foreground-muted not-italic text-2xs font-sans'>
                  {t('stored')}
                </span>
                <span className='text-foreground-base'>{issue.comparison.actual}</span>
              </span>
              <Icon name='arrow-right' className='h-2.5 w-2.5 text-foreground-muted shrink-0' />
              <span className='inline-flex items-center gap-xs font-mono bg-black/8 rounded px-xs py-px'>
                <span className='text-foreground-muted not-italic text-2xs font-sans'>
                  {t('calculated')}
                </span>
                <span className='text-foreground-base'>{issue.comparison.expected}</span>
              </span>
            </span>
          )}
          {onScrollTo && issue.path && (
            <Button
              variant='ghost'
              size='icon'
              onClick={() => onScrollTo(issue.path!)}
              className='ml-xs opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100 transition-opacity hover:bg-black/10 align-middle'
              aria-label={t('scrollTo')}
            >
              <Icon name='arrow-right-from-bracket' className='h-3 w-3 text-foreground-muted' />
            </Button>
          )}
        </p>
        {issue.action && <div className='mt-xs'>{issue.action}</div>}
      </div>
    </div>
  );
}
